import {
  ExportOptions,
  ExportPayloadSection,
  ExportWorkerOutbound,
  SerializeExportProgress,
  SerializeExportRequest,
} from '../types';
import { SerializeExportResult } from './serializeExportContent';
import { terminateExportWorker } from './exportWorkerInstance';

export const runInWorker = (
  worker: Worker,
  sections: ExportPayloadSection[],
  options: ExportOptions,
  onProgress: SerializeExportProgress | undefined,
  signal: AbortSignal | undefined
): Promise<SerializeExportResult> =>
  new Promise<SerializeExportResult>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Export cancelled', 'AbortError'));
      return;
    }

    const cleanup = (): void => {
      worker.removeEventListener('message', handleMessage);
      worker.removeEventListener('messageerror', handleMessageError);
      worker.removeEventListener('error', handleError);
      signal?.removeEventListener('abort', handleAbort);
    };

    const handleMessage = (event: MessageEvent<ExportWorkerOutbound>): void => {
      const message = event.data;

      if (message.type === 'progress') {
        onProgress?.(message.processedRows, message.totalRows);
        return;
      }

      if (message.type === 'result') {
        cleanup();
        resolve({
          bytes: message.bytes,
          extension: message.extension,
          mimeType: message.mimeType,
        });
        return;
      }

      if (message.type === 'error') {
        cleanup();
        reject(new Error(message.message));
      }
    };

    const handleError = (event: ErrorEvent): void => {
      // Without this the browser also reports the exception to window, crashing the app.
      event.preventDefault();
      cleanup();
      // Worker crashed mid-export: discard the instance so the next call re-instantiates it.
      terminateExportWorker();
      reject(new Error(event.message || 'Export worker error'));
    };

    // Without this the promise would never settle when a result fails to deserialize.
    const handleMessageError = (): void => {
      cleanup();
      terminateExportWorker();
      reject(new Error('Export worker message could not be deserialized'));
    };

    const handleAbort = (): void => {
      cleanup();
      // Termination is the only way to interrupt a running worker; a fresh one will be created next time.
      terminateExportWorker();
      reject(new DOMException('Export cancelled', 'AbortError'));
    };

    worker.addEventListener('message', handleMessage);
    worker.addEventListener('messageerror', handleMessageError);
    worker.addEventListener('error', handleError);
    signal?.addEventListener('abort', handleAbort);

    const request: SerializeExportRequest = {
      type: 'serialize',
      sections,
      options,
    };

    worker.postMessage(request);
  });
